// Made-up approval cards for judging the design (see lib/sample.ts). Real cards will come from the server.
import { sampleOnly } from '../../lib/sample';
import type { CardFile, Instruction } from '@serb/shared';
import type { Tone } from '../../components/tone';

export type ApprovalKind = 'quotation' | 'agent-message' | 'booking' | 'customer-message' | 'jcs';
export type ApprovalStatus = 'pending' | 'approved' | 'sent' | 'rejected' | 'failed';
/** What you can decide on a card: approving never sends anything */
export type Decision = 'approved' | 'rejected';
export type Urgency = 'High' | 'Medium' | 'Normal' | 'Low';

export const KIND_LABEL: Record<ApprovalKind, string> = {
  quotation: 'Customer quotation',
  'agent-message': 'Email to overseas agent',
  booking: 'Booking confirmation',
  'customer-message': 'Message to customer',
  jcs: 'JCS for review',
};
export const URGENCY_TONE: Record<Urgency, Tone> = { High: 'error', Medium: 'warning', Normal: 'info', Low: 'neutral' };
export const URGENCIES: Urgency[] = ['High', 'Medium', 'Normal', 'Low'];
export const STATUS_LABEL: Record<ApprovalStatus, string> = {
  pending: 'Pending',
  approved: 'Approved',
  sent: 'Sent',
  rejected: 'Rejected',
  failed: 'Failed',
};

/** The numbers behind a price card: what it costs us, and the price the assistant suggests */
export type Quote = {
  currency: 'USD' | 'EUR';
  costs: [label: string, usd: number][];
  suggested: number;
  recommendation: string;
  keyPoints: string[];
  shipment: [label: string, value: string][];
};

export type Approval = {
  id: string;
  srb: string;
  kind: ApprovalKind;
  tag: string;
  tagTone: Tone;
  title: string;
  /** the line under the title: customer · route */
  subtitle: string;
  /** the grey line in the list */
  summary: string;
  /** short text for Home's table */
  homeDescription: string;
  homeValue: string;
  age: string;
  urgency: Urgency;
  status: ApprovalStatus;
  created: string;
  requestedBy: string;
  to?: string;
  /** for an email: who is copied, the subject, and the files that go with it */
  cc?: string;
  subject?: string;
  files?: CardFile[];
  /** for an email: the subject of the email it answers (reply-all in that thread); empty = it goes out as a NEW EMAIL */
  replyIn?: string;
  /** for an email: it is also a draft in Spark right now, and whether he changed it there */
  inSpark?: boolean;
  editedInSpark?: boolean;
  /** what will be sent, for messages */
  body?: string;
  /** for a JCS: the cost lines Claude worked out, in the currency below */
  lines?: [description: string, amount: number][];
  linesCurrency?: string;
  quote?: Quote;
  history: [time: string, text: string][];
  /** what you told Claude to change on this card (real cards only) */
  instructions?: Instruction[];
};

const ABC_QUOTE: Quote = {
  currency: 'USD',
  costs: [
    ['Agent freight cost (air freight)', 1320],
    ['Origin charges', 45],
    ['Destination charges', 18],
  ],
  suggested: 1550,
  recommendation:
    'Based on the available agent rates, market rates for this route, and recent quotations to ABC Trading, I recommend quoting USD 1,550. This gives a 10.8% margin, which is within your target range (8–12%) and remains competitive.',
  keyPoints: [
    'Agent rate from Global Freight Ltd.',
    'Transit time: 4–5 days',
    'Rate valid until 28 Jul 2025',
    'Includes fuel and security charges',
    'No additional handling charges expected',
    'Customer previously accepted similar rates for this lane',
  ],
  shipment: [
    ['SRB Code', 'SRB-711'],
    ['Customer', 'ABC Trading'],
    ['Route', 'Guangzhou (CAN) → Amman (AMM)'],
    ['Transport Mode', 'Air Freight'],
    ['Cargo', 'General cargo, 222.25 kg'],
    ['Requested Transit Time', '4–6 days'],
    ['Special Requirements', '—'],
  ],
};

const NEXT = 'Created by SERB AI after reading the latest emails';
const HISTORY: Approval['history'] = [
  ['09:15', NEXT],
  ['09:15', 'Waiting for your decision'],
];

export const APPROVALS: Approval[] = sampleOnly([
  {
    id: 'a1',
    srb: 'SRB-711',
    kind: 'quotation',
    tag: 'Pricing',
    tagTone: 'error',
    title: 'Customer quotation',
    subtitle: 'ABC Trading · Guangzhou → Amman (Air)',
    summary: 'Suggested price: USD 1,550 · Margin: 10.8%',
    homeDescription: 'ABC Trading → Amman (Air)',
    homeValue: 'USD 1,550',
    age: '2h ago',
    urgency: 'High',
    status: 'pending',
    created: '23 Jul 2025, 09:15',
    requestedBy: 'SERB AI',
    quote: ABC_QUOTE,
    history: [...HISTORY, ['08:50', 'Agent rate received from Global Freight Ltd.']],
  },
  {
    id: 'a2',
    srb: 'SRB-640',
    kind: 'agent-message',
    tag: 'Agent Message',
    tagTone: 'info',
    title: 'Email to overseas agent',
    subtitle: 'Al-Rafedi · Germany → Dubai (Air)',
    summary: 'Request updated rates',
    to: 'Global Freight Ltd.',
    homeDescription: 'Request updated rates',
    homeValue: '—',
    age: '1 day ago',
    urgency: 'Medium',
    status: 'pending',
    created: '22 Jul 2025, 10:02',
    requestedBy: 'SERB AI',
    body: 'Dear team,\n\nCould you please send us your updated air freight rates for Germany to Dubai, 340 kg general cargo, ready next week?\n\nThank you,\nSERB Express',
    history: HISTORY,
  },
  {
    id: 'a3',
    srb: 'SRB-596',
    kind: 'booking',
    tag: 'Booking',
    tagTone: 'success',
    title: 'Booking confirmation',
    subtitle: 'CMA CGM · Shanghai → Aqaba (FCL)',
    summary: 'Confirm booking with carrier',
    to: 'CMA CGM',
    homeDescription: 'CMA CGM → Aqaba',
    homeValue: '—',
    age: '1 day ago',
    urgency: 'Medium',
    status: 'pending',
    created: '22 Jul 2025, 11:20',
    requestedBy: 'SERB AI',
    body: 'Please confirm our booking for 1 x 40HC, Shanghai to Aqaba, sailing 5 Aug, as agreed in the rate you gave us.',
    history: HISTORY,
  },
  {
    id: 'a4',
    srb: 'SRB-703',
    kind: 'customer-message',
    tag: 'Customer Message',
    tagTone: 'success',
    title: 'Response to customer',
    subtitle: 'Horizon Co. · Jebel Ali → Amman',
    summary: 'Re: Updated delivery schedule',
    to: 'Horizon Co.',
    homeDescription: 'Updated delivery schedule',
    homeValue: '—',
    age: '1 day ago',
    urgency: 'Normal',
    status: 'pending',
    created: '22 Jul 2025, 13:40',
    requestedBy: 'SERB AI',
    body: 'Dear Horizon team,\n\nYour shipment is now expected to arrive in Amman on 12 Aug. We will send the arrival notice as soon as it is available.\n\nBest regards,\nSERB Express',
    history: HISTORY,
  },
  {
    id: 'a5',
    srb: 'SRB-539',
    kind: 'jcs',
    tag: 'Financial',
    tagTone: 'warning',
    title: 'JCS for review',
    subtitle: 'France → Aqaba (FCL)',
    summary: 'Agent invoice and cost breakdown',
    homeDescription: 'Prepare JCS',
    homeValue: '—',
    age: '2 days ago',
    urgency: 'Normal',
    status: 'pending',
    created: '21 Jul 2025, 16:05',
    requestedBy: 'SERB AI',
    body: 'JCS workbook for SRB-539 is ready. It will be emailed to Sokaina only after you approve it.',
    lines: [
      ['Agent freight', 3200],
      ['Carrier charges', 500],
      ['Local costs', 150],
    ],
    linesCurrency: 'USD',
    history: HISTORY,
  },
  {
    id: 'a6',
    srb: 'SRB-648',
    kind: 'quotation',
    tag: 'Quotation',
    tagTone: 'error',
    title: 'Customer quotation',
    subtitle: 'TechnoPack · Italy → Amman (Air)',
    summary: 'Suggested price: EUR 3,800 · Margin: 12.5%',
    homeDescription: 'TechnoPack → Amman (Air)',
    homeValue: 'EUR 3,800',
    age: '2 days ago',
    urgency: 'Normal',
    status: 'pending',
    created: '21 Jul 2025, 12:10',
    requestedBy: 'SERB AI',
    quote: {
      ...ABC_QUOTE,
      currency: 'EUR',
      costs: [
        ['Agent freight cost (air freight)', 3240],
        ['Origin charges', 60],
        ['Destination charges', 25],
      ],
      suggested: 3800,
      shipment: [
        ['SRB Code', 'SRB-648'],
        ['Customer', 'TechnoPack'],
        ['Route', 'Italy → Amman (AMM)'],
        ['Transport Mode', 'Air Freight'],
      ],
    },
    history: HISTORY,
  },
  {
    id: 'a7',
    srb: 'SRB-625',
    kind: 'agent-message',
    tag: 'Agent Message',
    tagTone: 'info',
    title: 'Follow up with agent',
    subtitle: 'Sky Logistics',
    summary: 'Check container availability',
    to: 'Sky Logistics',
    homeDescription: 'Check container availability',
    homeValue: '—',
    age: '2 days ago',
    urgency: 'Low',
    status: 'pending',
    created: '21 Jul 2025, 09:30',
    requestedBy: 'SERB AI',
    body: 'Hi, a quick follow up: do you have 40HC containers available for the 5 Aug sailing? Please confirm.',
    history: HISTORY,
  },
  {
    id: 'a8',
    srb: 'SRB-518',
    kind: 'quotation',
    tag: 'Pricing',
    tagTone: 'error',
    title: 'Customer quotation',
    subtitle: 'Al-Rafedi · Spain → Aqaba (FCL)',
    summary: 'Suggested price: USD 3,380 · Margin: 9.8%',
    homeDescription: 'Al-Rafedi → Aqaba (FCL)',
    homeValue: 'USD 3,380',
    age: '3 days ago',
    urgency: 'Low',
    status: 'pending',
    created: '20 Jul 2025, 15:45',
    requestedBy: 'SERB AI',
    quote: {
      ...ABC_QUOTE,
      costs: [
        ['Ocean freight (40HC)', 2800],
        ['Origin charges', 160],
        ['Destination charges', 89],
      ],
      suggested: 3380,
      shipment: [
        ['SRB Code', 'SRB-518'],
        ['Customer', 'Al-Rafedi'],
        ['Route', 'Spain → Aqaba'],
        ['Transport Mode', 'Sea Freight (FCL)'],
      ],
    },
    history: HISTORY,
  },
  {
    id: 'a9',
    srb: 'SRB-702',
    kind: 'customer-message',
    tag: 'Customer Message',
    tagTone: 'success',
    title: 'Booking update to customer',
    subtitle: 'ABC Trading · Shanghai → Aqaba',
    summary: 'Approved, waiting to be sent',
    to: 'ABC Trading',
    homeDescription: '',
    homeValue: '—',
    age: '3h ago',
    urgency: 'Normal',
    status: 'approved',
    created: '23 Jul 2025, 07:30',
    requestedBy: 'SERB AI',
    body: 'Your booking is confirmed for the 5 Aug sailing.',
    history: [
      ['07:30', NEXT],
      ['08:10', 'You approved this.'],
    ],
  },
  {
    id: 's1',
    srb: 'SRB-640',
    kind: 'quotation',
    tag: 'Quotation',
    tagTone: 'error',
    title: 'Customer quotation',
    subtitle: 'Al-Rafedi · Germany → Dubai (Air)',
    summary: 'Sent: USD 2,400',
    to: 'Al-Rafedi',
    homeDescription: '',
    homeValue: 'USD 2,400',
    age: '1d ago',
    urgency: 'Normal',
    status: 'sent',
    created: '22 Jul 2025, 08:00',
    requestedBy: 'SERB AI',
    history: [
      ['08:00', NEXT],
      ['08:20', 'You approved this'],
      ['08:21', 'Sent to Al-Rafedi'],
    ],
  },
  {
    id: 's2',
    srb: 'SRB-596',
    kind: 'agent-message',
    tag: 'Agent Message',
    tagTone: 'info',
    title: 'Rate request',
    subtitle: 'Horizon Co. · UK → Aqaba (FCL)',
    summary: 'Sent to EuroTrans',
    to: 'EuroTrans',
    homeDescription: '',
    homeValue: '—',
    age: '2d ago',
    urgency: 'Low',
    status: 'sent',
    created: '21 Jul 2025, 09:00',
    requestedBy: 'SERB AI',
    history: [
      ['09:00', NEXT],
      ['09:10', 'You approved this'],
      ['09:11', 'Sent to EuroTrans'],
    ],
  },
  {
    id: 'r1',
    srb: 'SRB-530',
    kind: 'quotation',
    tag: 'Pricing',
    tagTone: 'error',
    title: 'Customer quotation',
    subtitle: 'TechnoPack · Spain → Aqaba (FCL)',
    summary: 'You rejected this price',
    homeDescription: '',
    homeValue: 'USD 3,100',
    age: '4d ago',
    urgency: 'Low',
    status: 'rejected',
    created: '19 Jul 2025, 10:00',
    requestedBy: 'SERB AI',
    history: [
      ['10:00', NEXT],
      ['10:30', 'You rejected this'],
    ],
  },
  {
    id: 'f1',
    srb: 'SRB-612',
    kind: 'customer-message',
    tag: 'Customer Message',
    tagTone: 'success',
    title: 'Arrival notice',
    subtitle: 'CMA Commercial · Amman',
    summary: 'Could not be sent: the email address was refused',
    to: 'CMA Commercial',
    homeDescription: '',
    homeValue: '—',
    age: '5d ago',
    urgency: 'Medium',
    status: 'failed',
    created: '18 Jul 2025, 14:00',
    requestedBy: 'SERB AI',
    history: [
      ['14:00', NEXT],
      ['14:10', 'You approved this'],
      ['14:11', 'Sending failed: address refused'],
    ],
  },
]);
